import { useState, useRef, useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, GitCommit, FileText } from "lucide-react";
import toast from "react-hot-toast";

import { commitsApi, CheckChangesResponse } from "@/lib/api/commits";

interface CommitBeforePublishModalProps {
  isOpen: boolean;
  projectId: string;
  /** Pre-fetched check result - modal won't fetch if provided */
  checkResult: CheckChangesResponse;
  onClose: () => void;
  onCommitAndPublish: () => void;
  onPublishWithoutCommit: () => void;
  isPublishing?: boolean;
}

export function CommitBeforePublishModal({
  isOpen,
  projectId,
  checkResult,
  onClose,
  onCommitAndPublish,
  onPublishWithoutCommit,
  isPublishing = false,
}: CommitBeforePublishModalProps) {
  const [isCommitting, setIsCommitting] = useState(false);
  const [commitMessage, setCommitMessage] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state and focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setCommitMessage("");
      setIsCommitting(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleCommitAndPublish = async () => {
    if (!commitMessage.trim()) {
      toast.error("Please enter a commit message");
      return;
    }

    setIsCommitting(true);
    try {
      await commitsApi.createCommit(projectId, commitMessage.trim());
      toast.success("Changes committed successfully");
      onCommitAndPublish();
    } catch (err: any) {
      const message =
        err.response?.data?.message || err.message || "Failed to create commit";

      // Handle "no changes" case gracefully
      if (message === "No changes to commit") {
        toast("No changes to commit", { icon: "ℹ️" });
        onPublishWithoutCommit();
      } else {
        toast.error(message);
        console.error("Commit error:", err);
      }
    } finally {
      setIsCommitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isCommitting && commitMessage.trim()) {
      handleCommitAndPublish();
    }
  };

  const isLoading = isCommitting || isPublishing;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitCommit className="h-5 w-5 text-amber-500" />
            Uncommitted Changes
          </DialogTitle>
          <DialogDescription>
            You have {checkResult.filesCount} file
            {checkResult.filesCount !== 1 ? "s" : ""} with uncommitted changes.
            Would you like to commit before publishing?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Changed files list */}
          <div className="rounded-md border border-border bg-muted/30 overflow-hidden">
            <div className="px-3 py-2 border-b border-border">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Changed Files
              </span>
            </div>
            <ScrollArea className="max-h-32">
              <div className="px-3 py-2 space-y-1">
                {checkResult.changedFiles.slice(0, 10).map((file) => (
                  <div
                    key={file}
                    className="flex items-center gap-2 text-xs text-muted-foreground min-w-0"
                  >
                    <FileText className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate min-w-0">{file}</span>
                  </div>
                ))}
                {checkResult.changedFiles.length > 10 && (
                  <div className="text-xs text-muted-foreground/70 pt-1">
                    ...and {checkResult.changedFiles.length - 10} more files
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Commit message input */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Commit Message</label>
            <Input
              ref={inputRef}
              value={commitMessage}
              onChange={(e) => setCommitMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe your changes..."
              disabled={isLoading}
              className="text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 pt-2">
            <Button
              onClick={handleCommitAndPublish}
              disabled={isLoading || !commitMessage.trim()}
              className="w-full"
            >
              {isCommitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Committing...
                </>
              ) : (
                "Commit & Publish"
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onPublishWithoutCommit}
              disabled={isLoading}
              className="w-full"
            >
              {isPublishing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                "Publish Without Committing"
              )}
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={isLoading}
              className="w-full text-muted-foreground"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
